import React, { useState, useEffect } from 'react'
import { collection, getDocs, doc, updateDoc, getDoc, addDoc, setDoc } from 'firebase/firestore'
import * as XLSX from 'xlsx'
import { db } from '../firebase'
import { useAuth } from '../contexts/AuthContext'
import { useNotifications } from '../contexts/NotificationContext'
import DeviceDetail from './DeviceDetail'
import AlertBadge from './AlertBadge'
import MessagesService from '../services/messagesService'

export default function DeviceList({ onRefresh, initialFilter, onFilterApplied }) {
  const { userClaims, userPermissions, isSuperAdmin } = useAuth()
  const [devices, setDevices] = useState([])
  const [loading, setLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState(null)
  const [customerName, setCustomerName] = useState('')
  const [selectedDevice, setSelectedDevice] = useState(null)
  const [updatingDevice, setUpdatingDevice] = useState(null)
  const [deviceStatuses, setDeviceStatuses] = useState({})
  const [showAddDeviceModal, setShowAddDeviceModal] = useState(false)
  const [deviceTemplate, setDeviceTemplate] = useState(null)
  const [availableTemplates, setAvailableTemplates] = useState([])
  const [selectedTemplateId, setSelectedTemplateId] = useState('')
  const [loadingTemplates, setLoadingTemplates] = useState(false)
  const [newDeviceData, setNewDeviceData] = useState('')
  const [newDeviceId, setNewDeviceId] = useState('')
  const [addingDevice, setAddingDevice] = useState(false)
  const [showDeviceLogsModal, setShowDeviceLogsModal] = useState(false)
  const [selectedDeviceForLogs, setSelectedDeviceForLogs] = useState(null)
  const [deviceLogs, setDeviceLogs] = useState([])
  const [loadingLogs, setLoadingLogs] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [stockFilter, setStockFilter] = useState('all')
  const [paymentBypassFilter, setPaymentBypassFilter] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(20)
  const [sortColumn, setSortColumn] = useState('')
  const [sortDirection, setSortDirection] = useState('asc')

  // Apply initial filter from parent component (e.g., Dashboard)
  useEffect(() => {
    if (initialFilter?.paymentBypass) {
      setPaymentBypassFilter(initialFilter.paymentBypass)
      // Notify parent that filter has been applied
      if (onFilterApplied) {
        onFilterApplied()
      }
    }
  }, [initialFilter, onFilterApplied])

  // Calculate stock level for a device
  const calculateStockLevel = (device) => {
    if (!device.slots || Object.keys(device.slots).length === 0) {
      return { percentage: 0, totalCapacity: 0, currentQuantity: 0 }
    }

    let totalCapacity = 0
    let currentQuantity = 0

    Object.values(device.slots).forEach(slot => {
      const capacity = slot.capacity || slot.quantity || 0
      const quantity = slot.quantity || 0
      totalCapacity += capacity
      currentQuantity += quantity
    })

    const percentage = totalCapacity > 0 ? Math.round((currentQuantity / totalCapacity) * 100) : 0
    return { percentage, totalCapacity, currentQuantity }
  }

  // Calculate total stock value (in INR) for a device
  const calculateStockValue = (device) => {
    if (!device.slots || Object.keys(device.slots).length === 0) {
      return 0
    }

    let totalValue = 0

    Object.values(device.slots).forEach(slot => {
      const quantity = slot.quantity || 0
      const price = slot.price || 0
      totalValue += quantity * price
    })

    return totalValue
  }

  // Format currency value in INR
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value)
  }

  // Get color class based on stock percentage
  const getStockColor = (percentage) => {
    if (percentage >= 80) return 'bg-success'
    if (percentage >= 30) return 'bg-warning'
    return 'bg-danger'
  }

  // Sortable column header
  const SortableHeader = ({ column, children }) => {
    const active = sortColumn === column
    const icon = active
      ? (sortDirection === 'asc' ? 'fa-sort-up' : 'fa-sort-down')
      : 'fa-sort'
    const handleClick = () => {
      if (active) {
        setSortDirection(d => d === 'asc' ? 'desc' : 'asc')
      } else {
        setSortColumn(column)
        setSortDirection('asc')
      }
      setCurrentPage(1)
    }
    return (
      <th
        onClick={handleClick}
        style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
      >
        {children}{' '}
        <i className={`fas ${icon} text-muted ms-1`} style={{ fontSize: '0.75rem', opacity: active ? 1 : 0.4 }}></i>
      </th>
    )
  }

  // Apply sort to an array of devices
  const applySortToDevices = (list) => {
    if (!sortColumn) return list
    return [...list].sort((a, b) => {
      let aVal, bVal
      switch (sortColumn) {
        case 'id':       aVal = a.id || ''; bVal = b.id || ''; break
        case 'stock':    aVal = calculateStockLevel(a).percentage; bVal = calculateStockLevel(b).percentage; break
        case 'value':    aVal = calculateStockValue(a); bVal = calculateStockValue(b); break
        case 'status':   aVal = deviceStatuses[a.id] || ''; bVal = deviceStatuses[b.id] || ''; break
        case 'maint':    aVal = a.maintenance_mode ? 1 : 0; bVal = b.maintenance_mode ? 1 : 0; break
        case 'location': aVal = a.location || ''; bVal = b.location || ''; break
        case 'updated': {
          const aTs = a.lastUpdated || a.last_updated
          const bTs = b.lastUpdated || b.last_updated
          aVal = aTs ? (aTs?.toDate?.()?.getTime() || new Date(aTs).getTime()) : 0
          bVal = bTs ? (bTs?.toDate?.()?.getTime() || new Date(bTs).getTime()) : 0
          break
        }
        default: return 0
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
  }

  // Export replenishment planning sheet (one row per slot across all devices)
  const exportReplenishmentPlan = () => {
    const data = applySortToDevices(getFilteredDevices())
    const rows = []
    data.forEach(device => {
      if (!device.slots || Object.keys(device.slots).length === 0) return
      Object.entries(device.slots).forEach(([slotId, slot]) => {
        const capacity = slot.capacity || 0
        const quantity = slot.quantity || 0
        const replenishCount = Math.max(0, capacity - quantity)
        const pctFull = capacity > 0 ? Math.round((quantity / capacity) * 100) : 0
        rows.push({
          'Machine ID': device.id,
          'Location': device.location || '',
          'Slot ID': slotId,
          'Product Name': slot.name || '',
          'Capacity': capacity,
          'Quantity': quantity,
          'Replenish Count': replenishCount,
          '% Full': pctFull,
        })
      })
    })
    const ws = XLSX.utils.json_to_sheet(rows)
    // Set column widths for readability
    ws['!cols'] = [
      { wch: 14 }, { wch: 22 }, { wch: 10 }, { wch: 28 },
      { wch: 10 }, { wch: 10 }, { wch: 16 }, { wch: 8 },
    ]
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Replenishment Plan')
    XLSX.writeFile(wb, `replenishment-plan-${new Date().toISOString().slice(0, 10)}.xlsx`)
  }

  // Export currently filtered devices to Excel
  const exportToExcel = () => {
    const data = applySortToDevices(getFilteredDevices())
    const rows = data.map(device => {
      const stock = calculateStockLevel(device)
      const value = calculateStockValue(device)
      return {
        'Device ID': device.id,
        'Stock Level (%)': stock.percentage,
        'Qty / Capacity': `${stock.currentQuantity}/${stock.totalCapacity}`,
        'Stock Value (₹)': value,
        'Status': deviceStatuses[device.id] || 'Unknown',
        'Maintenance Mode': device.maintenance_mode ? 'ON' : 'OFF',
        'Location': device.location || '',
        'Last Updated': (() => { const ts = device.lastUpdated || device.last_updated; return ts ? new Date(ts?.toDate?.() || ts).toLocaleString() : '' })(),
      }
    })
    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Devices')
    XLSX.writeFile(wb, `devices-${new Date().toISOString().slice(0, 10)}.xlsx`)
  }

  // Filter and sort devices
  const getFilteredDevices = () => {
    const filtered = devices.filter(device => {
      // Search filter (device ID, location, hardware make)
      if (searchText) {
        const searchLower = searchText.toLowerCase()
        const matchesId = device.id.toLowerCase().includes(searchLower)
        const matchesLocation = device.location?.toLowerCase().includes(searchLower)
        const matchesMake = device.hardware_config?.make?.toLowerCase().includes(searchLower)
        
        // Return false if none of the fields match
        if (!matchesId && !matchesLocation && !matchesMake) {
          return false
        }
      }

      // Status filter
      if (statusFilter !== 'all') {
        const status = deviceStatuses[device.id]
        if (statusFilter === 'online' && status !== 'online' && status !== 'up') return false
        if (statusFilter === 'offline' && status !== 'offline' && status !== 'down') return false
        if (statusFilter === 'maintenance' && !device.maintenance_mode) return false
      }

      // Stock level filter
      if (stockFilter !== 'all') {
        const stockLevel = calculateStockLevel(device).percentage
        if (stockFilter === 'well-stocked' && stockLevel < 80) return false
        if (stockFilter === 'low-stock' && (stockLevel < 30 || stockLevel >= 80)) return false
        if (stockFilter === 'critical' && stockLevel >= 30) return false
      }

      // Payment bypass filter
      if (paymentBypassFilter !== 'all') {
        const hasPaymentBypass = device.enable_purchase_button === true
        if (paymentBypassFilter === 'bypass-enabled' && !hasPaymentBypass) return false
        if (paymentBypassFilter === 'bypass-disabled' && hasPaymentBypass) return false
      }

      return true
    })
    return applySortToDevices(filtered)
  }

  // Check if any filters are active
  const hasActiveFilters = () => {
    return searchText !== '' || statusFilter !== 'all' || stockFilter !== 'all' || paymentBypassFilter !== 'all'
  }

  // Get paginated devices from filtered list
  const getPaginatedDevices = () => {
    const filtered = getFilteredDevices()
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return filtered.slice(startIndex, endIndex)
  }

  // Calculate total pages
  const getTotalPages = () => {
    return Math.ceil(getFilteredDevices().length / itemsPerPage)
  }

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [searchText, statusFilter, stockFilter, paymentBypassFilter, itemsPerPage])

  // Clear all filters
  const clearFilters = () => {
    setSearchText('')
    setStatusFilter('all')
    setStockFilter('all')
    setPaymentBypassFilter('all')
    setCurrentPage(1)
  }

  useEffect(() => {
    if (userClaims?.customer) {
      setCustomerName(userClaims.customer)
      fetchDevices(userClaims.customer)
      fetchDeviceStatuses(userClaims.customer)
    }
  }, [userClaims])

  const fetchDevices = async (customer) => {
    if (!customer) {
      setError('No customer name found in claims')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const collectionName = `${customer}_machines`
      console.log(`Fetching devices from collection: ${collectionName}`)
      
      const devicesRef = collection(db, collectionName)
      const snapshot = await getDocs(devicesRef)
      
      const deviceList = []
      snapshot.forEach((doc) => {
        const data = doc.data()
        
        
        deviceList.push({
          id: doc.id,
          ...data
        })
      })

      // Apply device_restrictions for non-super-admin users
      const restrictions = userPermissions?.device_restrictions
      const restricted = !isSuperAdmin && Array.isArray(restrictions) && restrictions.length > 0
      const visibleDevices = restricted
        ? deviceList.filter(d => restrictions.includes(d.id) || restrictions.includes(d.machine_name) || restrictions.includes(d.name))
        : deviceList

      setDevices(visibleDevices)
      console.log(`Found ${visibleDevices.length} devices for customer: ${customer}${restricted ? ` (restricted from ${deviceList.length})` : ''}`)
    } catch (err) {
      console.error('Error fetching devices:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const fetchDeviceStatuses = async (customer) => {
    if (!customer) return

    try {
      console.log(`Fetching device statuses for customer: ${customer}`)
      
      // Fetch the device_state document using customer name as document ID
      const deviceStateRef = doc(db, 'device_state', customer)
      const deviceStateSnap = await getDoc(deviceStateRef)
      
      if (deviceStateSnap.exists()) {
        const deviceStateData = deviceStateSnap.data()
        
        // Extract device statuses from the document
        const statuses = {}
        Object.keys(deviceStateData).forEach(key => {
          // Look for keys that match the pattern dev.{deviceId}.status
          if (key.startsWith('dev.') && key.endsWith('.status')) {
            const deviceId = key.replace('dev.', '').replace('.status', '')
            statuses[deviceId] = deviceStateData[key]
          } else if (deviceStateData[key] && typeof deviceStateData[key] === 'object' && deviceStateData[key].status) {
            // Handle direct device ID keys with status object
            statuses[key] = deviceStateData[key].status
          }
        })
        
        setDeviceStatuses(statuses)
        console.log('Device statuses loaded:', statuses)
      } else {
        console.log('No device state document found for customer:', customer)
        setDeviceStatuses({})
      }
    } catch (err) {
      console.error('Error fetching device statuses:', err)
      // Don't set error state as this is not critical
    }
  }

  const handleRefresh = async () => {
    setRefreshing(true)
    setError(null)
    
    try {
      // Always call the Dashboard refresh if provided
      if (onRefresh) {
        await onRefresh()
      }
      
      // Always refresh local data to ensure DeviceList state is updated
      if (customerName) {
        await Promise.all([
          fetchDevices(customerName),
          fetchDeviceStatuses(customerName)
        ])
      }
    } catch (err) {
      console.error('Error during refresh:', err)
      setError('Failed to refresh device data')
    } finally {
      setRefreshing(false)
    }
  }

  // Fetch device-specific activity logs
  const fetchDeviceLogs = async (deviceId) => {
    if (!customerName || !deviceId) return
    
    setLoadingLogs(true)
    try {
      const messagesService = new MessagesService(customerName)
      const logs = await messagesService.getDeviceMessages(deviceId, 50) // Get last 50 messages for this device
      setDeviceLogs(logs)
    } catch (error) {
      console.error('Error fetching device logs:', error)
      setDeviceLogs([])
    } finally {
      setLoadingLogs(false)
    }
  }

  // Handle View Logs button click
  const handleViewLogs = async (deviceId) => {
    setSelectedDeviceForLogs(deviceId)
    setShowDeviceLogsModal(true)
    await fetchDeviceLogs(deviceId)
  }

  // Fetch available templates from touch-device-templates collection
  const fetchAvailableTemplates = async () => {
    try {
      setLoadingTemplates(true)
      const templatesRef = collection(db, 'touch-device-templates')
      const snapshot = await getDocs(templatesRef)
      
      const templateList = []
      snapshot.forEach((doc) => {
        templateList.push({
          id: doc.id,
          ...doc.data()
        })
      })
      
      setAvailableTemplates(templateList)
      console.log(`Found ${templateList.length} templates in touch-device-templates`)
      
      // Auto-select the first template if available
      if (templateList.length > 0) {
        setSelectedTemplateId(templateList[0].id)
        await fetchSelectedTemplate(templateList[0].id)
      }
      
      return templateList
    } catch (err) {
      console.error('Error fetching available templates:', err)
      setError(`Failed to fetch templates: ${err.message}`)
      return []
    } finally {
      setLoadingTemplates(false)
    }
  }
  
  // Fetch specific template by ID
  const fetchSelectedTemplate = async (templateId) => {
    if (!templateId) {
      setDeviceTemplate(null)
      setNewDeviceData('')
      return
    }
    
    try {
      const templateRef = doc(db, 'touch-device-templates', templateId)
      const templateDoc = await getDoc(templateRef)
      
      if (templateDoc.exists()) {
        const templateData = templateDoc.data()
        setDeviceTemplate(templateData)
        setNewDeviceData(JSON.stringify(templateData, null, 2))
        return templateData
      } else {
        throw new Error(`Template '${templateId}' not found in touch-device-templates`)
      }
    } catch (err) {
      console.error('Error fetching selected template:', err)
      setError(`Failed to fetch template '${templateId}': ${err.message}`)
      return null
    }
  }

  // Handle add device modal
  const handleAddDevice = async () => {
    if (!userClaims?.sadmin) {
      setError('Only super administrators can add new devices')
      return
    }
    
    setShowAddDeviceModal(true)
    setNewDeviceId('')
    setNewDeviceData('')
    setDeviceTemplate(null)
    setSelectedTemplateId('')
    setError(null)
    
    // Fetch available templates
    await fetchAvailableTemplates()
  }

  // Create new device from template
  const handleCreateDevice = async () => {
    if (!newDeviceId.trim()) {
      setError('Device ID is required')
      return
    }

    if (!newDeviceData.trim()) {
      setError('Device data is required')
      return
    }

    try {
      setAddingDevice(true)
      setError(null)

      // Parse JSON data
      const deviceData = JSON.parse(newDeviceData)
      
      // Check if device ID already exists
      const existingDeviceRef = doc(db, `${customerName}_machines`, newDeviceId)
      const existingDevice = await getDoc(existingDeviceRef)
      
      if (existingDevice.exists()) {
        throw new Error(`Device with ID "${newDeviceId}" already exists`)
      }

      // Create new device document
      await setDoc(existingDeviceRef, deviceData)
      
      console.log(`Created new device: ${newDeviceId}`)
      
      // Refresh device list
      await fetchDevices(customerName)
      
      // Close modal and reset form
      setShowAddDeviceModal(false)
      setNewDeviceId('')
      setNewDeviceData('')
      setDeviceTemplate(null)
      setAvailableTemplates([])
      setSelectedTemplateId('')
      
      // Show success message
      setError(null)
      
    } catch (err) {
      console.error('Error creating device:', err)
      if (err instanceof SyntaxError) {
        setError('Invalid JSON format in device data')
      } else {
        setError(`Failed to create device: ${err.message}`)
      }
    } finally {
      setAddingDevice(false)
    }
  }

  const toggleMaintenanceMode = async (deviceId, currentMode) => {
    setUpdatingDevice(deviceId)
    setError(null)

    try {
      const collectionName = `${customerName}_machines`
      const deviceRef = doc(db, collectionName, deviceId)
      
      const newMode = !currentMode
      await updateDoc(deviceRef, {
        maintenance_mode: newMode
      })

      // Update local state
      setDevices(prevDevices =>
        prevDevices.map(device =>
          device.id === deviceId
            ? { ...device, maintenance_mode: newMode }
            : device
        )
      )

      console.log(`Device ${deviceId} maintenance mode ${newMode ? 'enabled' : 'disabled'}`)
    } catch (err) {
      console.error('Error toggling maintenance mode:', err)
      setError(`Failed to update maintenance mode: ${err.message}`)
    } finally {
      setUpdatingDevice(null)
    }
  }

  if (!userClaims) {
    return (
      <div className="alert alert-info">
        <i className="fas fa-info-circle me-2"></i>
        Loading user claims...
      </div>
    )
  }

  if (!customerName) {
    return (
      <div className="alert alert-warning">
        <i className="fas fa-exclamation-triangle me-2"></i>
        No customer name found in Firebase claims. Expected claim: "customer"
      </div>
    )
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h5 className="mb-1">
            Device Management
            <AlertBadge category="stockAlerts" className="ms-2" />
            <AlertBadge category="deviceOffline" className="ms-1" />
          </h5>
          <small className="text-muted">
            Device Registry: <code>{customerName}_machines</code>
          </small>
        </div>
        <div className="d-flex gap-2">
          {userClaims?.sadmin && (
            <button 
              className="btn btn-success btn-sm"
              onClick={handleAddDevice}
              disabled={loading}
            >
              <i className="fas fa-plus me-1"></i>
              Add Device
            </button>
          )}
          <button
            className="btn btn-outline-success btn-sm"
            onClick={exportToExcel}
            disabled={loading || devices.length === 0}
            title="Export filtered list to Excel"
          >
            <i className="fas fa-file-excel me-1"></i>
            Export Excel
          </button>
          <button
            className="btn btn-outline-warning btn-sm"
            onClick={exportReplenishmentPlan}
            disabled={loading || devices.length === 0}
            title="Download replenishment planning sheet (all slots)"
          >
            <i className="fas fa-clipboard-list me-1"></i>
            Replenishment Plan
          </button>
          <button 
            className="btn btn-outline-primary btn-sm"
            onClick={handleRefresh}
            disabled={loading || refreshing}
          >
            <i className={`fas ${refreshing ? 'fa-spinner fa-spin' : 'fa-sync-alt'} me-1`}></i>
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <strong>Error:</strong> {error}
          <br />
          <small>
            Make sure the device registry "{customerName}_machines" exists and you have access permissions.
          </small>
        </div>
      )}

      {/* Summary Cards */}
      {!loading && devices.length > 0 && (
        <>
          {/* Operational Status Cards - Row 1 */}
          <div className="row mb-3">
            <div className="col-md-3 mb-3">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="text-muted text-uppercase mb-1">Total Devices</h6>
                      <h3 className="mb-0">{devices.length}</h3>
                    </div>
                    <div className="text-primary">
                      <i className="fas fa-desktop fa-2x"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3 mb-3">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="text-muted text-uppercase mb-1">Online</h6>
                      <h3 className="mb-0 text-success">
                        {devices.filter(device => 
                          deviceStatuses[device.id] === 'up' || 
                          deviceStatuses[device.id] === 'online'
                        ).length}
                      </h3>
                    </div>
                    <div className="text-success">
                      <i className="fas fa-check-circle fa-2x"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3 mb-3">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="text-muted text-uppercase mb-1">Offline</h6>
                      <h3 className="mb-0 text-danger">
                        {devices.filter(device => 
                          deviceStatuses[device.id] === 'down' || 
                          deviceStatuses[device.id] === 'offline'
                        ).length}
                      </h3>
                    </div>
                    <div className="text-danger">
                      <i className="fas fa-times-circle fa-2x"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3 mb-3">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="text-muted text-uppercase mb-1">Maintenance</h6>
                      <h3 className="mb-0 text-warning">
                        {devices.filter(device => device.maintenance_mode).length}
                      </h3>
                    </div>
                    <div className="text-warning">
                      <i className="fas fa-wrench fa-2x"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3 mb-3">
              <div 
                className="card border-0 shadow-sm cursor-pointer" 
                style={{ cursor: 'pointer' }}
                onClick={() => setPaymentBypassFilter('bypass-enabled')}
                title="Click to filter payment bypass devices"
              >
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="text-muted text-uppercase mb-1">Payment Bypass</h6>
                      <h3 className="mb-0 text-danger">
                        {devices.filter(device => device.enable_purchase_button === true).length}
                      </h3>
                      <small className="text-muted">Free purchases enabled</small>
                    </div>
                    <div className="text-danger">
                      <i className="fas fa-unlock-alt fa-2x"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stock Level Cards - Row 2 */}
          <div className="row mb-4">
            <div className="col-md-3 mb-3">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="text-muted text-uppercase mb-1">Well Stocked</h6>
                      <h3 className="mb-0 text-success">
                        {devices.filter(device => calculateStockLevel(device).percentage >= 80).length}
                      </h3>
                      <small className="text-muted">≥80% stock level</small>
                    </div>
                    <div className="text-success">
                      <i className="fas fa-battery-full fa-2x"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3 mb-3">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="text-muted text-uppercase mb-1">Low Stock</h6>
                      <h3 className="mb-0 text-danger">
                        {devices.filter(device => calculateStockLevel(device).percentage < 30).length}
                      </h3>
                      <small className="text-muted">&lt;30% stock level</small>
                    </div>
                    <div className="text-danger">
                      <i className="fas fa-battery-quarter fa-2x"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3 mb-3">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="text-muted text-uppercase mb-1">Avg Stock Level</h6>
                      <h3 className="mb-0">
                        {(() => {
                          const avgStock = devices.length > 0 ? Math.round(
                            devices.reduce((sum, device) => sum + calculateStockLevel(device).percentage, 0) / devices.length
                          ) : 0
                          return (
                            <span className={avgStock >= 80 ? 'text-success' : avgStock >= 50 ? 'text-warning' : 'text-danger'}>
                              {avgStock}%
                            </span>
                          )
                        })()}
                      </h3>
                      <small className="text-muted">across all devices</small>
                    </div>
                    <div className="text-info">
                      <i className="fas fa-chart-bar fa-2x"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3 mb-3">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="text-muted text-uppercase mb-1">Total Stock Value</h6>
                      <h3 className="mb-0 text-success">
                        {formatCurrency(devices.reduce((sum, device) => sum + calculateStockValue(device), 0))}
                      </h3>
                      <small className="text-muted">across all devices</small>
                    </div>
                    <div className="text-success">
                      <i className="fas fa-coins fa-2x"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div>Loading devices from {customerName} registry...</div>
        </div>
      ) : (
        <>
          {devices.length > 0 && (
            <div className="card border-0 shadow-sm mb-3">
              <div className="card-body">
                <div className="row g-3 align-items-end">
                  <div className="col-md-3 col-lg-2">
                    <label className="form-label small text-muted mb-1">
                      <i className="fas fa-search me-1"></i>Search
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="ID, Location, Make..."
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label small text-muted mb-1">
                      <i className="fas fa-signal me-1"></i>Status
                    </label>
                    <select
                      className="form-select"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="all">All</option>
                      <option value="online">Online</option>
                      <option value="offline">Offline</option>
                      <option value="maintenance">Maintenance</option>
                    </select>
                  </div>
                  <div className="col-md-2">
                    <label className="form-label small text-muted mb-1">
                      <i className="fas fa-box me-1"></i>Stock
                    </label>
                    <select
                      className="form-select"
                      value={stockFilter}
                      onChange={(e) => setStockFilter(e.target.value)}
                    >
                      <option value="all">All</option>
                      <option value="well-stocked">≥80%</option>
                      <option value="low-stock">30-79%</option>
                      <option value="critical">&lt;30%</option>
                    </select>
                  </div>
                  <div className="col-md-2">
                    <label className="form-label small text-muted mb-1">
                      <i className="fas fa-unlock-alt me-1"></i>Bypass
                    </label>
                    <select
                      className="form-select"
                      value={paymentBypassFilter}
                      onChange={(e) => setPaymentBypassFilter(e.target.value)}
                    >
                      <option value="all">All</option>
                      <option value="bypass-enabled">Enabled</option>
                      <option value="bypass-disabled">Disabled</option>
                    </select>
                  </div>
                  <div className="col-md-2 col-lg-1">
                    <label className="form-label small text-muted mb-1">
                      <i className="fas fa-list-ol me-1"></i>Show
                    </label>
                    <select 
                      className="form-select"
                      value={itemsPerPage}
                      onChange={(e) => setItemsPerPage(parseInt(e.target.value))}
                    >
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                  <div className="col-md-1">
                    {hasActiveFilters() && (
                      <button
                        className="btn btn-outline-secondary w-100"
                        onClick={clearFilters}
                        title="Clear Filters"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                  </div>
                </div>
                {hasActiveFilters() && (
                  <div className="mt-3">
                    <small className="text-muted">
                      <i className="fas fa-filter me-1"></i>
                      {(() => {
                        const activeFilters = []
                        if (searchText) activeFilters.push(`Search: "${searchText}"`)
                        if (statusFilter !== 'all') activeFilters.push(`Status: ${statusFilter}`)
                        if (stockFilter !== 'all') activeFilters.push(`Stock: ${stockFilter}`)
                        if (paymentBypassFilter !== 'all') activeFilters.push(`Payment Bypass: ${paymentBypassFilter === 'bypass-enabled' ? 'Enabled' : 'Disabled'}`)
                        return `Active filters: ${activeFilters.join(', ')}`
                      })()}
                    </small>
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white">
              <h6 className="mb-0">
                Devices ({(() => {
                  const filtered = getFilteredDevices()
                  return hasActiveFilters() ? `${filtered.length} of ${devices.length}` : devices.length
                })()})
                {devices.length > 0 && (
                  <span className="badge bg-primary ms-2">{customerName}</span>
                )}
              </h6>
            </div>
          <div className="card-body p-0">
            {devices.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-desktop fa-3x mb-3 d-block opacity-50"></i>
                <p>No devices found in registry "{customerName}_machines"</p>
                <small>
                  Add some devices to this registry to see them here.
                </small>
              </div>
            ) : getFilteredDevices().length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-filter fa-3x mb-3 d-block opacity-50"></i>
                <p>No devices match the selected filters</p>
                <small>
                  Try adjusting your filters or
                  <button className="btn btn-link btn-sm p-0 ms-1" onClick={clearFilters}>
                    clear all filters
                  </button>
                </small>
              </div>
            ) : (
              <>
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="bg-light">
                    <tr>
                      <SortableHeader column="id">Device ID</SortableHeader>
                      <SortableHeader column="stock">Stock Level</SortableHeader>
                      <SortableHeader column="value">Stock Value (₹)</SortableHeader>
                      <SortableHeader column="status">Status</SortableHeader>
                      <SortableHeader column="maint">Maintenance Mode</SortableHeader>
                      <SortableHeader column="location">Location</SortableHeader>
                      <SortableHeader column="updated">Last Updated</SortableHeader>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getPaginatedDevices().map((device) => (
                      <tr key={device.id}>
                        <td>
                          <div className="d-flex flex-column align-items-start gap-1">
                            <code className="small">{device.id}</code>
                            <div className="d-flex flex-wrap gap-1">
                              {device.enable_purchase_button === true && (
                                <span 
                                  className="badge bg-danger" 
                                  title="Payment bypass enabled - free purchases allowed"
                                  style={{ fontSize: '0.7rem' }}
                                >
                                  <i className="fas fa-unlock-alt me-1"></i>
                                  Payment Bypass
                                </span>
                              )}
                              {(device.payments?.active?.includes('smartcard') || 
                                device.payments?.smartcard === true) && (
                                <i 
                                  className="fas fa-wifi text-success" 
                                  title="Smart Card payments enabled"
                                  style={{ fontSize: '0.85rem', transform: 'rotate(90deg)' }}
                                ></i>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          {(() => {
                            const stockData = calculateStockLevel(device)
                            const colorClass = getStockColor(stockData.percentage)
                            return (
                              <div className="stock-level-container" style={{ minWidth: '140px' }}>
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                  <small className="fw-bold">{stockData.percentage}%</small>
                                  <small className="text-muted">
                                    {stockData.currentQuantity}/{stockData.totalCapacity}
                                  </small>
                                </div>
                                <div className="progress" style={{ height: '8px' }}>
                                  <div 
                                    className={`progress-bar ${colorClass}`} 
                                    style={{ width: `${Math.max(stockData.percentage, 2)}%` }}
                                    role="progressbar"
                                    aria-valuenow={stockData.percentage}
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                  ></div>
                                </div>
                                {stockData.totalCapacity === 0 && (
                                  <small className="text-muted">No slots configured</small>
                                )}
                              </div>
                            )
                          })()}
                        </td>
                        <td>
                          <div className="fw-bold text-primary">
                            {formatCurrency(calculateStockValue(device))}
                          </div>
                          {calculateStockValue(device) === 0 && (
                            <small className="text-muted">No items</small>
                          )}
                        </td>
                        <td>
                          <span className={`badge ${
                            deviceStatuses[device.id] === 'up' ? 'bg-success' :
                            deviceStatuses[device.id] === 'online' ? 'bg-success' :
                            deviceStatuses[device.id] === 'down' ? 'bg-danger' :
                            deviceStatuses[device.id] === 'offline' ? 'bg-danger' :
                            deviceStatuses[device.id] === 'maintenance' ? 'bg-warning text-dark' :
                            deviceStatuses[device.id] === 'error' ? 'bg-danger' :
                            deviceStatuses[device.id] === 'idle' ? 'bg-info' :
                            deviceStatuses[device.id] === 'busy' ? 'bg-primary' :
                            'bg-secondary'
                          }`}>
                            {deviceStatuses[device.id] || 'Unknown'}
                          </span>
                          {deviceStatuses[device.id] && (
                            <div className="small text-muted mt-1">
                              <i className="fas fa-sync-alt me-1"></i>
                              Real-time
                            </div>
                          )}
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <button
                              className={`btn btn-sm ${
                                device.maintenance_mode
                                  ? 'btn-warning'
                                  : 'btn-outline-secondary'
                              }`}
                              onClick={() => toggleMaintenanceMode(device.id, device.maintenance_mode)}
                              disabled={updatingDevice === device.id}
                              title={device.maintenance_mode ? 'Disable Maintenance Mode' : 'Enable Maintenance Mode'}
                            >
                              {updatingDevice === device.id ? (
                                <i className="fas fa-spinner fa-spin"></i>
                              ) : (
                                <i className={`fas fa-${device.maintenance_mode ? 'lock' : 'unlock'}`}></i>
                              )}
                            </button>
                            <span className={`badge ${
                              device.maintenance_mode ? 'bg-warning text-dark' : 'bg-success'
                            }`}>
                              {device.maintenance_mode ? 'ON' : 'OFF'}
                            </span>
                          </div>
                        </td>
                        <td>{device.location || 'Not specified'}</td>
                        <td>
                          {(device.lastUpdated || device.last_updated) ? (
                            <small>
                              {new Date((device.lastUpdated || device.last_updated)?.toDate?.() || (device.lastUpdated || device.last_updated)).toLocaleString()}
                            </small>
                          ) : (
                            <small className="text-muted">Never</small>
                          )}
                        </td>
                        <td>
                          <div className="btn-group btn-group-sm">
                            <button 
                              className="btn btn-outline-primary"
                              title="View Details & Configure"
                              onClick={() => setSelectedDevice(device.id)}
                            >
                              <i className="fas fa-cog"></i>
                            </button>
                            <button 
                              className="btn btn-outline-secondary"
                              title="Edit Layout"
                              onClick={() => setSelectedDevice(device.id)}
                              disabled={!device.maintenance_mode}
                            >
                              <i className="fas fa-th-large"></i>
                            </button>
                            <button 
                              className="btn btn-outline-info"
                              title="View Logs"
                              onClick={() => handleViewLogs(device.id)}
                            >
                              <i className="fas fa-list"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </>
            )}
          </div>
          {getFilteredDevices().length > 0 && getTotalPages() > 1 && (
            <div className="card-footer d-flex justify-content-between align-items-center">
              <div className="text-muted small">
                Showing {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, getFilteredDevices().length)} of {getFilteredDevices().length} devices
              </div>
              <nav>
                <ul className="pagination mb-0">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button 
                      className="page-link"
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                    >
                      <i className="fas fa-angle-double-left"></i>
                    </button>
                  </li>
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button 
                      className="page-link"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                  </li>
                  <li className="page-item active">
                    <span className="page-link">
                      {currentPage} of {getTotalPages()}
                    </span>
                  </li>
                  <li className={`page-item ${currentPage === getTotalPages() ? 'disabled' : ''}`}>
                    <button 
                      className="page-link"
                      onClick={() => setCurrentPage(prev => Math.min(getTotalPages(), prev + 1))}
                      disabled={currentPage === getTotalPages()}
                    >
                      Next
                    </button>
                  </li>
                  <li className={`page-item ${currentPage === getTotalPages() ? 'disabled' : ''}`}>
                    <button 
                      className="page-link"
                      onClick={() => setCurrentPage(getTotalPages())}
                      disabled={currentPage === getTotalPages()}
                    >
                      <i className="fas fa-angle-double-right"></i>
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </div>
        </>
      )}

      {/* Add Device Modal - Super Admin Only */}
      {showAddDeviceModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-plus-circle me-2"></i>
                  Add New Device
                  <span className="badge bg-danger ms-2">Super Admin</span>
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowAddDeviceModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info mb-4">
                  <i className="fas fa-info-circle me-2"></i>
                  <strong>Creating device from template:</strong> Select a template from the <code>touch-device-templates</code> collection below, then customize the JSON configuration for your new device.
                </div>

                {error && (
                  <div className="alert alert-danger mb-4">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    <strong>Error:</strong> {error}
                  </div>
                )}

                <div className="row">
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label htmlFor="deviceId" className="form-label">
                        <strong>Device ID</strong>
                        <small className="text-muted d-block">Unique identifier for the new device</small>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="deviceId"
                        value={newDeviceId}
                        onChange={(e) => setNewDeviceId(e.target.value)}
                        placeholder="e.g., VM001, AV001, etc."
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label htmlFor="templateSelect" className="form-label">
                        <strong>Select Template</strong>
                        <small className="text-muted d-block">Choose from available device templates</small>
                      </label>
                      <select
                        className="form-select"
                        id="templateSelect"
                        value={selectedTemplateId}
                        onChange={(e) => {
                          setSelectedTemplateId(e.target.value)
                          fetchSelectedTemplate(e.target.value)
                        }}
                        disabled={loadingTemplates}
                      >
                        <option value="">-- Select Template --</option>
                        {availableTemplates.map((template) => (
                          <option key={template.id} value={template.id}>
                            {template.name || template.id}
                          </option>
                        ))}
                      </select>
                      {loadingTemplates && (
                        <div className="text-center mt-2">
                          <div className="spinner-border spinner-border-sm me-2"></div>
                          <small className="text-muted">Loading templates...</small>
                        </div>
                      )}
                    </div>
                    
                    <div className="card">
                      <div className="card-header">
                        <h6 className="mb-0">Template Information</h6>
                      </div>
                      <div className="card-body">
                        {deviceTemplate && selectedTemplateId ? (
                          <div>
                            <p><strong>Collection:</strong> touch-device-templates</p>
                            <p><strong>Template:</strong> {selectedTemplateId}</p>
                            <p className="mb-0"><strong>Target:</strong> {customerName}_machines/{newDeviceId || '[Device ID]'}</p>
                            {availableTemplates.find(t => t.id === selectedTemplateId)?.description && (
                              <div className="mt-2">
                                <small className="text-muted">
                                  <strong>Description:</strong> {availableTemplates.find(t => t.id === selectedTemplateId).description}
                                </small>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-center text-muted">
                            {loadingTemplates ? (
                              <>
                                <div className="spinner-border spinner-border-sm me-2"></div>
                                Loading templates...
                              </>
                            ) : (
                              <small>Select a template to view details</small>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-md-8">
                    <div className="mb-3">
                      <label htmlFor="deviceData" className="form-label">
                        <strong>Device Configuration (JSON)</strong>
                        <small className="text-muted d-block">Edit the template data to configure your new device</small>
                      </label>
                      <textarea
                        className="form-control font-monospace"
                        id="deviceData"
                        value={newDeviceData}
                        onChange={(e) => setNewDeviceData(e.target.value)}
                        rows="20"
                        style={{ fontSize: '13px' }}
                        placeholder="Loading device template..."
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowAddDeviceModal(false)}
                  disabled={addingDevice}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleCreateDevice}
                  disabled={addingDevice || !newDeviceId.trim() || !newDeviceData.trim()}
                >
                  {addingDevice ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Creating Device...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-plus me-1"></i>
                      Create Device
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Device Logs Modal */}
      {showDeviceLogsModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-list me-2"></i>
                  Device Activity Logs
                  {selectedDeviceForLogs && (
                    <>
                      <span className="badge bg-info ms-2">{selectedDeviceForLogs}</span>
                      {(() => {
                        const device = devices?.find(d => d.id === selectedDeviceForLogs)
                        return device?.location ? (
                          <span className="badge bg-secondary ms-2">
                            <i className="fas fa-map-marker-alt me-1"></i>
                            {device.location}
                          </span>
                        ) : null
                      })()}
                    </>
                  )}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowDeviceLogsModal(false)
                    setSelectedDeviceForLogs(null)
                    setDeviceLogs([])
                  }}
                ></button>
              </div>
              <div className="modal-body">
                {loadingLogs ? (
                  <div className="text-center py-5">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <div className="mt-3">Loading device activity logs...</div>
                  </div>
                ) : deviceLogs && deviceLogs.length > 0 ? (
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <small className="text-muted">
                        Showing {deviceLogs.length} recent activities for device {selectedDeviceForLogs}
                      </small>
                      <button 
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => fetchDeviceLogs(selectedDeviceForLogs)}
                      >
                        <i className="fas fa-sync me-1"></i>
                        Refresh
                      </button>
                    </div>
                    <div className="list-group" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                      {deviceLogs.map((log, index) => (
                        <div key={log.id || index} className="list-group-item border-0 px-0 py-3">
                          <div className="d-flex align-items-start">
                            <div className="me-3">
                              <i className={MessagesService.getActionIcon(log.type)}></i>
                            </div>
                            <div className="flex-grow-1">
                              <div className="d-flex justify-content-between align-items-start">
                                <div>
                                  <h6 className="mb-1 fw-semibold">{log.type}</h6>
                                  <div className="d-flex align-items-center text-muted small mb-2">
                                    <span className={MessagesService.getSourceColor(log.source)}>
                                      <i className="fas fa-circle me-1" style={{ fontSize: '0.5rem' }}></i>
                                      {log.source}
                                    </span>
                                    {log.source === 'smart-cards' && log.cardId && (
                                      <>
                                        <span className="mx-2">•</span>
                                        <i className="fas fa-credit-card me-1"></i>
                                        <span>Card: {log.cardId}</span>
                                      </>
                                    )}
                                    {log.userEmail && (
                                      <>
                                        <span className="mx-2">•</span>
                                        <i className="fas fa-user me-1"></i>
                                        <span>{log.userEmail}</span>
                                      </>
                                    )}
                                  </div>
                                  <small className="text-muted">
                                    <i className="fas fa-clock me-1"></i>
                                    {MessagesService.formatRelativeTime(log.dateTime)}
                                  </small>
                                  <br />
                                  <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                    <i className="fas fa-calendar me-1"></i>
                                    {MessagesService.formatActualTime(log.dateTime)}
                                  </small>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-5 text-muted">
                    <i className="fas fa-history fa-3x mb-3 d-block"></i>
                    <h5>No Activity Found</h5>
                    <p>No recent activity logs found for device "{selectedDeviceForLogs}". Activities will appear here once operations are performed on this device.</p>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDeviceLogsModal(false)
                    setSelectedDeviceForLogs(null)
                    setDeviceLogs([])
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Device Detail Modal */}
      {selectedDevice && (
        <DeviceDetail
          deviceId={selectedDevice}
          onClose={() => setSelectedDevice(null)}
          onDeviceUpdated={() => {
            handleRefresh()
            setSelectedDevice(null)
          }}
        />
      )}
    </div>
  )
}